import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { DocumentAttachments } from '../../components/document-attachments';
import { isRichTextEmpty } from '../../components/rich-text-utils';
import { toDateInputValue, toDateTimeInputValue } from '../../../../utils/date';
import { parseNumberInput } from '../../../../utils/format';
import { STATUS_LICITACAO_LABEL, StatusLicitacao, enumOptions } from '../../../../model/apm/apm-enums';
import { ILookupResponse } from '../../../../model/apm/base/lookup-response';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { IBiddingAddEditRequest } from '../../../../model/apm/bidding/request/bidding-add-edit-request';
import { IBiddingResponse } from '../../../../model/apm/bidding/response/bidding-response';

interface BiddingDrawerProps {
    open: boolean;
    initial?: IBiddingResponse;
    files: IPortalDocumentResponse[];
    modalities: ILookupResponse[];
    managementUnits: ILookupResponse[];
    documentTypes: ILookupResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IBiddingAddEditRequest) => void;
    onUploadFile: (documentTypeId: number, file: File, onProgress: (percent: number) => void) => Promise<void>;
    onDeleteFile: (file: IPortalDocumentResponse) => void;
}

// Registros legados podem apontar para itens que não estão mais na lista; mantém a opção para não perder o vínculo
function withCurrent(options: ILookupResponse[], id?: number, description?: string): ILookupResponse[] {
    return id && !options.some((o) => o.id === id) ? [...options, { id, description: description || `Código ${id}` }] : options;
}

export function BiddingDrawer({
    open, initial, files, modalities, managementUnits, documentTypes, isSaving, onClose, onSave, onUploadFile, onDeleteFile,
}: BiddingDrawerProps) {
    const [form, setForm] = useState<IBiddingAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const amountInvalid = (form.homologatedAmount ?? 0) < 0;
    const errors = {
        number: showValidationErrors && !form.number?.trim(),
        modalityId: showValidationErrors && !form.modalityId,
        managementUnitId: showValidationErrors && !form.managementUnitId,
        status: showValidationErrors && !form.status,
        publishDate: showValidationErrors && !form.publishDate,
        openingDate: showValidationErrors && !form.openingDate,
        description: showValidationErrors && isRichTextEmpty(form.description),
        homologatedAmount: showValidationErrors && amountInvalid,
    };

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                number: initial.number,
                modalityId: initial.modalityId,
                managementUnitId: initial.managementUnitId,
                status: initial.status,
                description: initial.description,
                publishDate: toDateInputValue(initial.publishDate),
                openingDate: toDateTimeInputValue(initial.openingDate),
                limitDate: toDateTimeInputValue(initial.limitDate),
                homologatedAmount: initial.homologatedAmount,
            }
            : { status: StatusLicitacao.EmAndamento });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IBiddingAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        const invalid = !form.number?.trim() || !form.modalityId || !form.managementUnitId || !form.status
            || !form.publishDate || !form.openingDate || isRichTextEmpty(form.description) || amountInvalid;
        if (invalid) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, number: form.number!.trim(), limitDate: form.limitDate || undefined });
    }

    const modalityOptions = withCurrent(modalities, initial?.modalityId, initial?.modality);
    const unitOptions = withCurrent(managementUnits, initial?.managementUnitId, initial?.managementUnit);

    return (
        <FormDrawer
            open={open}
            icon={<GavelIcon />}
            title={isEdit ? 'Editar licitação' : 'Nova licitação'}
            subtitle={isEdit ? `${initial?.number || ''}${initial?.modality ? ` · ${initial.modality}` : ''}` : 'Salve a licitação para anexar os documentos'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar licitação'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Número</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            placeholder="Ex.: 012/2026"
                            inputProps={{ maxLength: 150 }}
                            value={form.number || ''}
                            onChange={(e) => update({ number: e.target.value })}
                            error={errors.number}
                        />
                        {errors.number && <FormHelperText error>Número é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Situação</InputLabel>
                        <Select fullWidth value={form.status ?? ''} onChange={(e) => update({ status: Number(e.target.value) })} error={errors.status}>
                            {enumOptions(STATUS_LICITACAO_LABEL).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                        {errors.status && <FormHelperText error>Situação é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Modalidade</InputLabel>
                        <Select
                            fullWidth
                            value={form.modalityId ?? ''}
                            onChange={(e) => update({ modalityId: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={errors.modalityId}
                        >
                            {modalityOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.description}</MenuItem>)}
                        </Select>
                        {errors.modalityId && <FormHelperText error>Modalidade é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Unidade gestora</InputLabel>
                        <Select
                            fullWidth
                            value={form.managementUnitId ?? ''}
                            onChange={(e) => update({ managementUnitId: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={errors.managementUnitId}
                        >
                            {unitOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.description}</MenuItem>)}
                        </Select>
                        {errors.managementUnitId
                            ? <FormHelperText error>Unidade gestora é obrigatória</FormHelperText>
                            : unitOptions.length === 0 && <FormHelperText>Nenhuma unidade gestora cadastrada para a entidade</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Datas">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Publicação</InputLabel>
                        <OutlinedInput fullWidth type="date" value={form.publishDate || ''} onChange={(e) => update({ publishDate: e.target.value || undefined })} error={errors.publishDate} />
                        {errors.publishDate && <FormHelperText error>Data de publicação é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Abertura</InputLabel>
                        <OutlinedInput fullWidth type="datetime-local" value={form.openingDate || ''} onChange={(e) => update({ openingDate: e.target.value || undefined })} error={errors.openingDate} />
                        {errors.openingDate && <FormHelperText error>Data e hora de abertura são obrigatórias</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Data limite</InputLabel>
                        <OutlinedInput fullWidth type="datetime-local" value={form.limitDate || ''} onChange={(e) => update({ limitDate: e.target.value || undefined })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Objeto e valor">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Objeto</InputLabel>
                        <RichTextEditor value={form.description} onChange={(description) => update({ description })} error={errors.description} minHeight={160} />
                        {errors.description && <FormHelperText error>Objeto é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Valor homologado</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            value={form.homologatedAmount ?? ''}
                            onChange={(e) => update({ homologatedAmount: parseNumberInput(e.target.value) })}
                            error={errors.homologatedAmount}
                        />
                        {errors.homologatedAmount
                            ? <FormHelperText error>O valor não pode ser negativo</FormHelperText>
                            : <FormHelperText>Informe após a homologação</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Documentos" description="Edital, atas, resultados e demais arquivos publicados no portal. Os anexos são salvos na hora.">
                <DocumentAttachments
                    id="bidding-file"
                    files={files}
                    documentTypes={documentTypes}
                    disabledMessage={isEdit ? undefined : 'Salve a licitação para anexar documentos. O formulário continua aberto após salvar.'}
                    onUpload={onUploadFile}
                    onDelete={onDeleteFile}
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
